import LoggerService from 'modules/Logger';
import Dexie from 'dexie';

const oldDbName = 'BodDB';

class ScripsDB extends Dexie {
  private static OPERATIONS_THRESHOLD = 1000;
  private operationsCount = 0;
  private lastRefreshTime: number = Date.now();
  private static TIME_THRESHOLD = 60000; // 1 minute
  private static SCHEMA = {
    scrips:
      'id, data.exchangeToken, [data.instrumentType+data.segment+data.underlyingExchangeToken]',
    metadata: 'key',
  };

  constructor() {
    super('ScripsDB');
    this.version(1).stores(ScripsDB.SCHEMA);
  }

  async performSafeOperation<T>(
    operation: () => Promise<T>,
  ): Promise<T | null> {
    try {
      this.operationsCount++;

      LoggerService.log('BOD', 'operationsCount', this.operationsCount);

      // Check both operation count AND time since last refresh
      if (
        this.operationsCount > ScripsDB.OPERATIONS_THRESHOLD ||
        Date.now() - this.lastRefreshTime > ScripsDB.TIME_THRESHOLD
      ) {
        await this.refreshConnection();
      }

      if (!this.isOpen()) {
        LoggerService.log('BOD', 'Database is closed. Reopening...');
        await this.open();
      }
      return await operation(); // Return the result of the operation
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (error?.name === 'DatabaseClosedError') {
        LoggerService.error(
          'DatabaseClosedError occurred. Attempting to reopen...',
        );
        await this.open();
        return operation(); // Retry the operation and return the result
      }
      LoggerService.error(error);
      return null; // Return null if an unhandled error occurs
    }
  }

  private async refreshConnection(): Promise<void> {
    LoggerService.log('BOD', 'Db is refreshing connection');
    const startTime = new Date();
    if (this.isOpen()) {
      this.close();
      await this.open();
    }
    this.operationsCount = 0;
    this.lastRefreshTime = Date.now();
    const endTime = new Date();
    LoggerService.log(
      'BOD',
      'Db connection refreshed in',
      endTime.getTime() - startTime.getTime(),
      'ms',
    );
  }

  async updateMetadata(key: string, value: MetadataValue): Promise<void> {
    const metadata: MetadataRow = { key, value };

    await this.performSafeOperation(() =>
      this.table<MetadataRow>('metadata').put(metadata),
    );

    LoggerService.log(
      'BOD',
      `Updated metadata for key: ${key} with value: ${value}`,
    );
  }

  async getMetadata(key: string): Promise<MetadataValue | null> {
    const timeoutDuration = 100;

    let timeoutId: NodeJS.Timeout | undefined; // Initialize as undefined

    // Create a timeout promise that resolves with a value-compatible structure
    const timeoutPromise = new Promise<{ value: 'in-progress' }>(resolve => {
      timeoutId = global.setTimeout(() => {
        LoggerService.log(
          'BOD',
          `getMetadata for key: ${key} timed out after ${timeoutDuration}ms.`,
        );
        resolve({ value: 'in-progress' });
      }, timeoutDuration);
    });

    try {
      // Perform the actual database operation
      const operationPromise = this.performSafeOperation(() =>
        this.table<MetadataRow>('metadata').get({ key }),
      );

      // Race the two promises
      const result = await Promise.race([operationPromise, timeoutPromise]);

      return result?.value ?? null; // Safely access the value property
    } finally {
      // Ensure timeout is cleared after operation completes
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  }

  async bulkPutScrips(data: ScripDbRow[]): Promise<void> {
    const startTime = new Date();
    await this.performSafeOperation(() => this.table('scrips').bulkAdd(data));
    const endTime = new Date();
    LoggerService.log(
      'BOD',
      `Inserted ${data.length} scrips in ${endTime.getTime() -
        startTime.getTime()} ms.`,
    );
  }

  async checkForData(): Promise<boolean> {
    const startTime = new Date().getTime();
    const result = await this.performSafeOperation(async () => {
      const count = await this.table('scrips').count();
      return count > 0;
    });
    const endTime = new Date().getTime();

    LoggerService.log(
      'BOD',
      ` checkForData Query completed in ${endTime -
        startTime} ms. Result: ${result}`,
    );

    return result ?? false; // Default to `false` if null is returned
  }

  async recreateDatabase(existingDbs: string[]): Promise<void> {
    const startTime = new Date().getTime();
    try {
      // Check for the existence of the database
      if (existingDbs.includes('ScripsDB')) {
        LoggerService.log(
          'BOD',
          'Recreating database: Deleting existing database...',
        );
        await Dexie.delete('ScripsDB');
      }

      // Recreate and initialize the database
      const db = new Dexie('ScripsDB');
      db.version(1).stores(ScripsDB.SCHEMA);

      await db.open(); // Open the database (optional as Dexie auto-opens on first use)
      LoggerService.log('BOD', 'Database recreated successfully');
    } catch (error) {
      LoggerService.error(error);
    } finally {
      const endTime = new Date().getTime();
      LoggerService.log(
        'BOD',
        'Performance: recreateDatabase completed in',
        endTime - startTime,
        'ms',
      );
    }
  }

  async getDatabaseNames(): Promise<string[]> {
    return (
      (await this.performSafeOperation(() => Dexie.getDatabaseNames())) || []
    );
  }

  async clearOldDatabase(existingDbs: string[]): Promise<void> {
    const startTime = new Date().getTime();
    try {
      LoggerService.log('BOD', 'Existing databases:', existingDbs);

      if (existingDbs.includes(oldDbName)) {
        LoggerService.log(
          'BOD',
          `Found old database (${oldDbName}). Deleting...`,
        );
        const oldDb = new Dexie(oldDbName);
        await oldDb.delete();
        LoggerService.log('BOD', `${oldDbName} has been successfully deleted.`);
      } else {
        LoggerService.log(
          'BOD',
          `${oldDbName} does not exist. No action needed.`,
        );
      }
    } catch (error) {
      LoggerService.error(error);
    } finally {
      const endTime = new Date().getTime();
      LoggerService.log(
        'BOD',
        'Performance: clearOldDatabase completed in',
        endTime - startTime,
        'ms',
      );
    }
  }

  async getDBSearchResults(
    tableName: string,
    indexKey: string,
    searchKey: string[],
  ): Promise<ScripDbRow[]> {
    const result = await this.performSafeOperation(async () =>
      this.table(tableName)
        .where(indexKey)
        .equals(searchKey)
        .toArray(),
    );

    return (result || []) as ScripDbRow[];
  }

  async getScripSearchResult(token: string, property: 'exchangeToken' | 'id') {
    const startTime = new Date().getTime();

    const result: ScripDbRow | null = await this.performSafeOperation(
      async () => {
        if (property === 'id') {
          // Query directly by the primary key
          return this.table('scrips').get(token);
        }
        if (property === 'exchangeToken') {
          // Query using an indexed property
          return this.table('scrips')
            .where('data.exchangeToken')
            .equals(token)
            .first();
        }
        return null; // Fallback in case of unexpected property
      },
    );

    const endTime = new Date().getTime();

    LoggerService.log(
      'BOD',
      `Performance: Completed fetch query for scrip search ${token} in`,
      endTime - startTime,
      'ms',
    );

    return result?.data || null;
  }

  async getDexieDatabaseSize(): Promise<number> {
    // Use Promise.all to handle async calls for all tables in parallel
    const tableSizes = await Promise.all(
      this.tables.map(async (table) => {
        // Get all records from the table
        const allRecords = await table.toArray();

        // Calculate the size of the records
        return allRecords.reduce((total: number, record: any) => {
          const recordSize: number = new Blob([JSON.stringify(record)]).size;
          return total + recordSize;
        }, 0);
      }),
    );

    // Sum up the sizes of all tables
    return tableSizes.reduce((total, size) => total + size, 0);
  }
}

export default new ScripsDB();
